import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { getOutboundIps } from 'redux/features/outboundIp/outboundIpSlice'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'

export interface State {
  isLoading: boolean
  isFailed: boolean
  isSuccess: boolean
  isDirtyForm: boolean
  hasNoDomainPolicyAccess: boolean
}

export interface EventHandlers {
  helpConfig: {
    isOpen: boolean
    onHelpClick: () => void
    onCloseHelp: () => void
  }
  setIsTableDirty: (isDirty: boolean) => void
}

export type UseSenderIpLogic = [State, EventHandlers]

export const useSenderIpLogic = (): UseSenderIpLogic => {
  const dispatch = useAppDispatch()
  const [isTableDirty, setIsTableDirty] = useState(false)
  const [isHelpDialogOpened, setIsHelpDialogOpened] = useState<boolean>(false)

  const {
    pdDomainId,
    isGetOutboundIpsPending,
    isGetOutboundIpsSuccess,
    isGetOutboundIpsFailed,
    isRemoveOutboundIpsPending,
    isRemoveOutboundIpsSuccess,
    isAddOutboundIpsPending,
    isAddOutboundIpsSuccess,
    accessTokenObject
  } = useAppSelector(_stores => ({
    pdDomainId: _stores.auth.accessTokenObject?.pdDomainId,
    accessTokenObject: _stores.auth.accessTokenObject,
    outboundIps: _stores.outboundIp.outboundIps,
    isGetOutboundIpsPending: isPending(_stores.outboundIp.getOutboundIpApiStatus),
    isGetOutboundIpsSuccess: isSuccess(_stores.outboundIp.getOutboundIpApiStatus),
    isGetOutboundIpsFailed: isFailed(_stores.outboundIp.getOutboundIpApiStatus),
    isRemoveOutboundIpsPending: isPending(_stores.outboundIp.removeOutboundIpApiStatus),
    isRemoveOutboundIpsSuccess: isSuccess(_stores.outboundIp.removeOutboundIpApiStatus),
    isAddOutboundIpsPending: isPending(_stores.outboundIp.addOutboundIpApiStatus),
    isAddOutboundIpsSuccess: isSuccess(_stores.outboundIp.addOutboundIpApiStatus)
  }))

  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const isInDomainManageMode = !!pdDomainId
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(
    Number(accessTokenObject?.pdDomainId) || 0
  )
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    if (pdDomainId) {
      dispatch(getOutboundIps({ pdDomainId }))
    } else {
      dispatch(getOutboundIps({}))
    }
    // eslint-disable-next-line
  }, [pdDomainId, hasNoDomainPolicyAccess])

  useEffect(() => {
    if (isRemoveOutboundIpsSuccess || isAddOutboundIpsSuccess) {
      if (pdDomainId) {
        dispatch(getOutboundIps({ pdDomainId }))
      } else {
        dispatch(getOutboundIps({}))
      }
    }
    // eslint-disable-next-line
  }, [isRemoveOutboundIpsSuccess, isAddOutboundIpsSuccess])

  const onHelpClick = useCallback(() => {
    setIsHelpDialogOpened(true)
  }, [])

  const onCloseHelp = useCallback(() => {
    setIsHelpDialogOpened(false)
  }, [])

  return useMemo(
    () => [
      {
        isLoading: isGetOutboundIpsPending || isRemoveOutboundIpsPending || isAddOutboundIpsPending,
        isFailed: isGetOutboundIpsFailed,
        isSuccess: isGetOutboundIpsSuccess,
        isDirtyForm: isTableDirty,
        hasNoDomainPolicyAccess
      },
      {
        helpConfig: {
          isOpen: isHelpDialogOpened,
          onHelpClick,
          onCloseHelp
        },
        setIsTableDirty
      }
    ],
    [
      isGetOutboundIpsPending,
      isRemoveOutboundIpsPending,
      isAddOutboundIpsPending,
      isGetOutboundIpsFailed,
      isGetOutboundIpsSuccess,
      isTableDirty,
      isHelpDialogOpened,
      onHelpClick,
      onCloseHelp,
      hasNoDomainPolicyAccess
    ]
  )
}
